import * as React from 'react';
import { match } from 'react-router';
import { parse as parseQueryString } from 'query-string';

import { CustomDateRangeModal } from 'century-core/core-components/CustomDateRange/CustomDateRangeModal';

import { useAuth } from 'century-core/core-auth/hooks/useAuth';
import { hasFeature } from 'century-core/core-auth/utils';

import { initialiseData, TeacherDashboardBaseData } from 'century-core/features-dashboard/TeacherDashboard/api';
import AppHeaderContext from 'century-core/core-components/AppHeader/AppHeaderContext';
import { useLocation } from 'react-router-dom';
import { TimeSpanKey } from '../../utils/timeSpanUtils';
import { DateRange } from 'century-core/core-apis/learn/aggregations/aggregations';
import { MixpanelEventTypes } from 'century-core/core-utils/utils/mixpanel/MixpanelEventTypes';
import { MixpanelKeys } from 'century-core/core-utils/utils/mixpanel/MixpanelKeys';
import { DateTime } from 'luxon';
import { CLASS_COURSE_LIST } from 'century-core/core-apis/roentgen/queries/teacherDashboard';
import { getCourse } from 'century-core/core-apis/contentV2/courses/courses';
import { TeacherDashboardSelectedItemsContext } from './TeacherDashboardSelectedItemsContext';
import { ErrorReload } from 'century-core/core-components/ErrorReload/ErrorReload';
import { useQuery } from '@apollo/client';
import * as SentryLib from 'century-core/core-utils/lib/sentry';
import { useClassCategories } from 'century-core/core-utils/contexts/class-categories.context';
import mixpanel from 'mixpanel-browser';

// collection of values to retain filter selections across pages
interface TeacherDashboardCommonFilters {
  subjectId?: string;
  subjectGroupId?: string;
  timeSpan?: string; // potentially need additional options to custom dates
  courseId?: string;
  nuggetId?: string; // possibly not required?
  startDate?: string;
  endDate?: string;
}

export type TeacherDashboardBaseDataWithCourse = TeacherDashboardBaseData & {
  courses?: (Ctek.Roentgen.CourseInfo & { extendedData?: Ctek.Course })[];
};

export interface TeacherDashboardContextProps extends Partial<TeacherDashboardCommonFilters> {
  baseData?: TeacherDashboardBaseDataWithCourse | undefined;
  classId: string;
  isRTLLocale: boolean;
  contextLoading: boolean;
  setShowCustomDatePicker: (param: boolean) => void;
  dataQueryString?: string;
  updateTeacherDashboardFilters: React.Dispatch<React.SetStateAction<Partial<TeacherDashboardCommonFilters>>>;
  aggregatedCourseData?: { [id: string]: Ctek.Course };
}

const setQueryStringParams = (params: Partial<TeacherDashboardCommonFilters>) => {
  const searchParams = new URLSearchParams(window.location.search);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined) {
      searchParams.set(k, v);
    } else {
      searchParams.delete(k);
    }
  });
  if (
    (searchParams.get('timeSpan') === TimeSpanKey.CustomRange || searchParams.get('timeSpan') === TimeSpanKey.CurrentAcademicYear) &&
    (!searchParams.get('startDate') || !searchParams.get('endDate'))
  ) {
    searchParams.delete('timeSpan');
    searchParams.delete('startDate');
    searchParams.delete('endDate');
  }
  window.history.replaceState({}, '', window.location.pathname + '?' + searchParams.toString());
};

interface TeacherDashboardContextProviderProps {
  children: React.ReactNode;
  classId: string;
  match: match;
  isRTLLocale: boolean;
}

export const TeacherDashboardContext = React.createContext<TeacherDashboardContextProps>({
  isRTLLocale: false,
} as TeacherDashboardContextProps);

const DEFAULT_EARLIEST_YEAR = 2016;

const getContextQueryParams = (
  search: string,
  classCategory?: Ctek.ClassCategory,
  forcedTimeSpan?: TimeSpanKey
): TeacherDashboardCommonFilters => {
  const queryParams = new URLSearchParams(search);
  const courseId = queryParams.get('courseId') || undefined;
  // TODO Polymer assignments page subject filter breaks this currently
  const subjectId = queryParams.get('subjectId') || undefined;
  const subjectGroupId = queryParams.get('subjectGroupId') || undefined;
  const nuggetId = queryParams.get('nuggetId') || undefined;
  let timeSpan = queryParams.get('timeSpan') || undefined;
  // if junk data is included in query string it needs to be overridden here
  if (!Object.values(TimeSpanKey).includes(timeSpan as TimeSpanKey)) {
    timeSpan = forcedTimeSpan ?? (classCategory ? TimeSpanKey.CurrentAcademicYear : undefined);
  }
  let startDate = timeSpan === TimeSpanKey.CustomRange ? queryParams.get('startDate') || undefined : undefined;
  let endDate = timeSpan === TimeSpanKey.CustomRange ? queryParams.get('endDate') || undefined : undefined;
  if (startDate && !!DateTime.fromISO(startDate).toUTC().invalidExplanation) {
    startDate = undefined;
  }
  if (endDate && !!DateTime.fromISO(endDate).toUTC().invalidExplanation) {
    endDate = undefined;
  }
  return {
    courseId,
    subjectId,
    subjectGroupId,
    timeSpan,
    nuggetId,
    startDate: startDate ?? (timeSpan === TimeSpanKey.CurrentAcademicYear ? classCategory?.startDate : undefined),
    endDate: endDate ?? (timeSpan === TimeSpanKey.CurrentAcademicYear ? classCategory?.endDate : undefined),
  };
};

const useInitialData = (classId: string, accessToken: string) => {
  const [isLoading, setLoadingInitialData] = React.useState<boolean>(true);
  const [baseData, setBaseData] = React.useState<TeacherDashboardBaseDataWithCourse>({} as TeacherDashboardBaseDataWithCourse);
  const [error, setError] = React.useState<any>(null);
  const queryParams = React.useMemo(
    () => ({
      variables: {
        input: {
          classId,
          courseTypes: ['standard'],
        },
      },
      fetchPolicy: 'network-only' as any,
    }),
    [classId]
  );
  const coursesQuery = useQuery(CLASS_COURSE_LIST, queryParams);
  React.useEffect(() => {
    initialiseData(accessToken, classId)
      .then(({ classInfo, studyGroups, users }: any) => {
        setBaseData((prevBaseData: any) => ({
          ...prevBaseData,
          users,
          classInfo,
          studyGroups,
        }));
      })
      .catch(err => {
        SentryLib.captureExceptionWithScope(new Error(err?.message), 'error', { tags: { classId } });
        setError(err);
      });
  }, [classId, accessToken]);

  React.useEffect(() => {
    if (!coursesQuery.loading && baseData.users) {
      setLoadingInitialData(false);
    }
  }, [coursesQuery, baseData]);

  return React.useMemo(
    () => ({
      isLoading,
      error,
      data: {
        ...baseData,
        courses: coursesQuery?.data?.classCourseList.courses.map((v: any) => ({ ...v, strands: [] })),
      },
    }),
    [coursesQuery?.data, isLoading, baseData, error]
  );
};

export const TeacherDashboardContextProvider = (props: TeacherDashboardContextProviderProps) => {
  const { addBreadcrumb } = React.useContext(AppHeaderContext);
  const { classCategories, currentClassCategory } = useClassCategories();
  const { classId, isRTLLocale, match } = props;
  const location = useLocation();

  const { setSelectedClass } = React.useContext(TeacherDashboardSelectedItemsContext);

  const auth = useAuth();
  const accessToken = auth.accessToken || '';
  const { isLoading: isLoadingContext, data: baseData, error: baseDataError } = useInitialData(classId, accessToken);
  const forceDefaultTimeSpan =
    hasFeature(auth, 'teach', 'defaultToAllTimeFilter') || currentClassCategory?._id !== baseData.classInfo?.classCategory
      ? TimeSpanKey.AllTime
      : undefined;
  const queryParamsRef = React.useRef<Partial<TeacherDashboardCommonFilters>>(
    getContextQueryParams(location.search, currentClassCategory, forceDefaultTimeSpan)
  );
  const [showCustomDatePicker, setShowCustomDatePicker] = React.useState(false);
  const [persistentFilters, setPersistentFilters] = React.useState<Partial<TeacherDashboardCommonFilters>>(queryParamsRef.current);
  const [unappliedDate, setUnappliedDate] = React.useState({} as DateRange);

  const [aggregatedCourseData, setAggregatedCourseData] = React.useState<{ [id: string]: Ctek.Course }>({});

  const updateFilters = React.useCallback(
    (updatedParams: Partial<TeacherDashboardCommonFilters>) => {
      // if any filter requires an update
      if (Object.entries(updatedParams).some(([k, v]) => v !== persistentFilters?.[k])) {
        const params = {
          ...persistentFilters,
          ...updatedParams,
          ...(updatedParams.timeSpan === TimeSpanKey.CurrentAcademicYear && {
            startDate: currentClassCategory?.startDate,
            endDate: currentClassCategory?.endDate,
          }),
        };
        setPersistentFilters({
          ...params,
          startDate:
            (params.timeSpan === TimeSpanKey.CustomRange || params.timeSpan === TimeSpanKey.CurrentAcademicYear) && params.endDate
              ? params.startDate
              : undefined,
          endDate:
            (params.timeSpan === TimeSpanKey.CustomRange || params.timeSpan === TimeSpanKey.CurrentAcademicYear) && params.startDate
              ? params.endDate
              : undefined,
        });
      }
    },
    [currentClassCategory?.endDate, currentClassCategory?.startDate, persistentFilters]
  );

  const firstRun = React.useRef(true);

  // Initial data fetch
  React.useEffect(() => {
    if (firstRun.current && accessToken) {
      firstRun.current = false;
      const queryStringCourseId = parseQueryString(window.location.search).courseId as string | undefined;
      setPersistentFilters({
        ...queryParamsRef.current,
        courseId: queryStringCourseId,
      });
    }
  }, [accessToken]);

  React.useEffect(() => {
    if (baseData?.classInfo?.name && baseData?.classInfo?._id) {
      setSelectedClass({ id: baseData.classInfo._id, name: baseData.classInfo.name });
      addBreadcrumb({
        path: match.url,
        title: baseData.classInfo.name,
      });
    }
  }, [addBreadcrumb, baseData?.classInfo?._id, baseData?.classInfo?.name, match.url, setSelectedClass]);

  // update query string on filter change
  React.useEffect(() => {
    setQueryStringParams(persistentFilters);
  }, [persistentFilters]);

  // update filters on location change query params
  React.useEffect(() => {
    const newQueryData = getContextQueryParams(location.search, currentClassCategory, forceDefaultTimeSpan);

    if (
      // standard query string changes
      newQueryData.courseId !== queryParamsRef.current.courseId ||
      newQueryData.subjectId !== queryParamsRef.current.subjectId ||
      newQueryData.subjectGroupId !== queryParamsRef.current.subjectGroupId ||
      newQueryData.timeSpan !== queryParamsRef.current.timeSpan ||
      newQueryData.nuggetId !== queryParamsRef.current.nuggetId ||
      // additional checks for custom dates
      (newQueryData.timeSpan === TimeSpanKey.CustomRange &&
        (newQueryData.startDate !== queryParamsRef.current.startDate || newQueryData.endDate !== queryParamsRef.current.endDate))
    ) {
      setPersistentFilters(newQueryData);
    }

    if (queryParamsRef.current) {
      queryParamsRef.current = newQueryData;
    }
    setShowCustomDatePicker(false);
  }, [currentClassCategory, forceDefaultTimeSpan, location.search]);

  // gather extended course data for old pages as needed
  React.useEffect(() => {
    if (!!persistentFilters.courseId && !aggregatedCourseData[persistentFilters.courseId]) {
      getCourse(persistentFilters.courseId, accessToken)
        .then(courseData =>
          setAggregatedCourseData(priorState => ({
            ...priorState,
            [persistentFilters.courseId as string]: courseData,
          }))
        )
        .catch(() => {
          // in case the courseId is invalid, clear the field
          setPersistentFilters(filters => {
            const { courseId, ...remainingFilters } = filters;
            return remainingFilters;
          });
        });
    }
  }, [accessToken, persistentFilters.courseId, aggregatedCourseData]);

  const dataQueryString = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    Object.entries(persistentFilters).forEach(([k, v]) => {
      if (v) {
        params.set(k, v);
      } else if (v === null) {
        params.delete(k);
      }
    });
    return params.toString();
  }, [location, persistentFilters]);

  const setCustomDate = React.useCallback(() => {
    updateFilters({
      startDate: unappliedDate.startDate
        ? DateTime.fromISO(unappliedDate.startDate!).toUTC().toFormat('yyyy-MM-dd')
        : DateTime.local(2016).toUTC().toFormat('yyyy-MM-dd'),
      endDate: unappliedDate.endDate
        ? DateTime.fromISO(unappliedDate.endDate!).toUTC().toFormat('yyyy-MM-dd')
        : DateTime.local().toUTC().toFormat('yyyy-MM-dd'),
      timeSpan: TimeSpanKey.CustomRange,
    });
    setShowCustomDatePicker(false);
    mixpanel.track(MixpanelEventTypes.TeacherDashboardOverviewTimeFilterChanged, {
      [MixpanelKeys.ClassId]: classId,
      [MixpanelKeys.SelectedTimeFilter]: 'CustomRange',
    });
  }, [unappliedDate, classId, updateFilters]);

  if (baseDataError) {
    return <ErrorReload error={baseDataError} />;
  }

  const earliestClassDate = classCategories.map(eachClass => eachClass.startDate).sort()[0];
  const classStartYear = earliestClassDate ? new Date(earliestClassDate).getFullYear() : DEFAULT_EARLIEST_YEAR;

  return (
    <TeacherDashboardContext.Provider
      value={{
        baseData,
        isRTLLocale,
        setShowCustomDatePicker,
        contextLoading: isLoadingContext,
        classId,
        dataQueryString,
        updateTeacherDashboardFilters: updateFilters,
        aggregatedCourseData,
        ...persistentFilters,
      }}
    >
      {props.children}
      <CustomDateRangeModal
        earliestYear={classStartYear}
        apply={(selectedDateRange: DateRange) => {
          setUnappliedDate(selectedDateRange);
        }}
        initialSelection={
          persistentFilters.timeSpan === TimeSpanKey.CustomRange
            ? { start: persistentFilters.startDate!, end: persistentFilters.endDate! }
            : undefined
        }
        setCustomDate={setCustomDate}
        showCustomDatePicker={showCustomDatePicker}
        setShowCustomDatePicker={setShowCustomDatePicker}
      />
    </TeacherDashboardContext.Provider>
  );
};
